<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>
          <v-alert v-if="success" type="success" class="mb-4" closable @click:close="success = ''">
            {{ success }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else-if="!isAdmin" class="camp-denied">
            Esta sección es exclusiva del super administrador de Somos Teatro.
          </div>

          <template v-else>
            <div class="camp-title">
              <h1>{{ campaignId ? 'CAMPAÑA' : 'NUEVA CAMPAÑA' }}</h1>
              <h2>{{ stepTitle }}</h2>
            </div>

            <div class="camp-steps">
              <button
                v-for="(label, index) in stepLabels"
                :key="label"
                type="button"
                class="camp-step"
                :class="{ 'camp-step-on': step === index + 1, 'camp-step-done': step > index + 1 }"
                @click="goToStep(index + 1)"
              >
                <span class="camp-step-num">{{ index + 1 }}</span>
                <span class="camp-step-label">{{ label }}</span>
              </button>
            </div>

            <div class="camp-panel">
              <div class="camp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-email-fast-outline</v-icon>
                <span>{{ stepTitle }}</span>
              </div>

              <div class="camp-panel-body">
                <!-- Paso 1: nombre y tipo de match -->
                <template v-if="step === 1">
                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">NOMBRE</span>
                      <span class="camp-field-hint">ESCRIBA EL NOMBRE DE LA CAMPAÑA</span>
                    </div>
                    <div class="camp-field-control">
                      <v-text-field
                        v-model="form.name"
                        placeholder="Ej: Comedias verano 2024"
                        variant="outlined"
                        hide-details="auto"
                        :error-messages="nameError"
                        @update:model-value="nameError = ''"
                      />
                      <p class="camp-note">
                        * RECOMENDACIÓN: DEFINA UN NOMBRE DESCRIPTIVO Y FÁCIL DE RECORDAR
                      </p>
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">TIPO DE MATCH</span>
                      <span class="camp-field-hint">DEFINA EL TIPO DE MATCH</span>
                    </div>
                    <!-- Bloqueado a propósito: hoy el tipo de match no cambia
                         el resultado de la búsqueda (sólo si las obras y los
                         clientes vienen preseleccionados o no), así que no se
                         deja elegir hasta definir qué debe hacer cada opción. -->
                    <div class="camp-field-control camp-field-locked">
                      <label
                        v-for="option in matchOptions"
                        :key="option.value"
                        class="camp-radio"
                        :class="{ 'camp-radio-on': form.match_type === option.value }"
                      >
                        <input
                          type="radio"
                          disabled
                          :value="option.value"
                          :checked="form.match_type === option.value"
                        />
                        <span class="camp-radio-dot"></span>
                        <span class="camp-radio-text">
                          <strong>{{ option.label }}</strong>
                          <small>{{ option.description }}</small>
                        </span>
                      </label>

                      <p class="camp-pending">
                        <v-icon size="15">mdi-alert-circle-outline</v-icon>
                        PENDIENTE DE ACLARACIÓN: por ahora las dos opciones hacen
                        la misma búsqueda, así que el tipo de match queda fijo en
                        "{{ lockedMatchLabel }}" y no se puede cambiar.
                      </p>
                    </div>
                  </div>
                </template>

                <!-- Paso 2: parámetros del match -->
                <template v-else-if="step === 2">
                  <p class="camp-section-title">DEFINA LOS PARÁMETROS PARA EL MATCH:</p>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">GÉNERO</span>
                      <span class="camp-field-hint">SELECCIONE EL TIPO DE GÉNERO DE LA OBRA</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.genre"
                        :items="options.genres"
                        item-title="label"
                        item-value="value"
                        placeholder="Todos"
                        variant="outlined"
                        hide-details
                        clearable
                        :menu-props="{ maxHeight: 520 }"
                      />
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">TEMPORADA</span>
                      <span class="camp-field-hint">ÉPOCA DEL AÑO PREFERIDA PARA FUNCIONES</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.season"
                        :items="options.seasons"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">ESCENARIO</span>
                      <span class="camp-field-hint">SUPERFICIE DEL ESCENARIO (MTS)</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.stage_surface"
                        :items="stageSurfaceItems"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">PRESUPUESTO</span>
                      <span class="camp-field-hint">MONTO MÁXIMO POR FUNCIÓN</span>
                    </div>
                    <div class="camp-field-control">
                      <v-text-field
                        v-model.number="form.budget_max"
                        type="number"
                        min="0"
                        placeholder="Ninguno"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">RANGO ETÁREO</span>
                      <span class="camp-field-hint">DEFINA RANGO DE EDAD</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.age_range"
                        :items="options.age_ranges"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                  </div>

                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-title">TIPO DE CLIENTE</span>
                      <span class="camp-field-hint">SELECCIONE</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.client_type"
                        :items="clientTypeItems"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        hide-details
                        :menu-props="{ maxHeight: 520 }"
                      />
                    </div>
                  </div>
                </template>

                <!-- Paso 3: selección de envío -->
                <template v-else-if="step === 3">
                  <p class="camp-section-title">TIPO DE ENVÍO</p>
                  <div class="camp-field">
                    <div class="camp-field-label">
                      <span class="camp-field-hint">SELECCIONE CANTIDAD DE OBRAS EN EL CORREO</span>
                    </div>
                    <div class="camp-field-control">
                      <v-select
                        v-model="form.plays_per_email"
                        :items="playsPerEmailItems"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        hide-details
                        @update:model-value="trimSelectedPlays"
                      />
                    </div>
                  </div>

                  <div v-if="matching" class="d-flex justify-center py-6">
                    <v-progress-circular indeterminate color="primary" size="36" />
                  </div>

                  <template v-else>
                    <!-- OBRAS: los listados vienen paginados del backend, así que
                         acá sólo se muestran los de la página actual. -->
                    <div class="camp-list-head">
                      <p class="camp-section-subtitle">
                        {{ matchResult.plays_count }} OBRAS ENCONTRADAS
                      </p>
                      <div class="camp-search">
                        <input
                          v-model="playSearch"
                          class="camp-search-input"
                          type="text"
                          placeholder="Buscar por nombre de obra"
                          @input="onPlaySearch"
                          @keyup.enter="searchPlays"
                        />
                        <button
                          v-if="playSearch"
                          type="button"
                          class="camp-search-clear"
                          aria-label="Limpiar búsqueda"
                          @click="clearPlaySearch"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div class="camp-cards">
                      <button
                        v-for="play in matchResult.plays"
                        :key="play.id"
                        type="button"
                        class="camp-card"
                        :class="{ 'camp-card-on': selectedPlays.includes(play.id) }"
                        @click="togglePlay(play.id)"
                      >
                        <span class="camp-card-title">{{ play.title }}</span>
                        <span class="camp-card-sub">{{ play.company_name }}</span>
                        <span class="camp-card-meta">{{ seasonLabel(play) }}</span>
                      </button>
                      <p v-if="!matchResult.plays.length" class="camp-empty-inline">
                        {{ playSearch
                          ? 'Ninguna obra coincide con la búsqueda.'
                          : 'No hay obras que cumplan los parámetros elegidos.' }}
                      </p>
                    </div>
                    <div v-if="matchResult.plays_pages > 1" class="camp-pager">
                      <button
                        type="button"
                        class="camp-pager-btn"
                        :disabled="!matchResult.plays_has_previous"
                        @click="goPlayPage(-1)"
                      >
                        ANTERIOR
                      </button>
                      <span class="camp-pager-info">
                        PÁGINA {{ matchResult.plays_page }} DE {{ matchResult.plays_pages }}
                      </span>
                      <button
                        type="button"
                        class="camp-pager-btn"
                        :disabled="!matchResult.plays_has_next"
                        @click="goPlayPage(1)"
                      >
                        SIGUIENTE
                      </button>
                    </div>
                    <p class="camp-note">
                      SELECCIONE LAS OBRAS QUE DESEA ENVIAR ({{ form.plays_per_email }}
                      {{ form.plays_per_email === 1 ? 'OBRA' : 'OBRAS' }} COMO MÁXIMO).
                      <span class="camp-note-count">
                        {{ selectedPlays.length }} SELECCIONADA{{ selectedPlays.length === 1 ? '' : 'S' }}
                      </span>
                    </p>

                    <!-- CLIENTES -->
                    <div class="camp-list-head camp-section-spaced">
                      <p class="camp-section-subtitle">
                        {{ matchResult.clients_count }} CLIENTES ENCONTRADOS
                      </p>
                      <div class="camp-search">
                        <input
                          v-model="clientSearch"
                          class="camp-search-input"
                          type="text"
                          placeholder="Buscar por razón social"
                          @input="onClientSearch"
                          @keyup.enter="searchClients"
                        />
                        <button
                          v-if="clientSearch"
                          type="button"
                          class="camp-search-clear"
                          aria-label="Limpiar búsqueda"
                          @click="clearClientSearch"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div class="camp-cards">
                      <button
                        v-for="client in matchResult.clients"
                        :key="client.id"
                        type="button"
                        class="camp-card"
                        :class="{ 'camp-card-on': selectedClients.includes(client.id) }"
                        @click="toggleClient(client.id)"
                      >
                        <span class="camp-card-title">{{ client.name }}</span>
                        <span class="camp-card-sub">{{ client.company || client.client_type || 'SIN RUBRO' }}</span>
                        <span class="camp-card-meta">{{ client.city || '—' }}</span>
                      </button>
                      <p v-if="!matchResult.clients.length" class="camp-empty-inline">
                        {{ clientSearch
                          ? 'Ningún cliente coincide con la búsqueda.'
                          : 'No hay clientes que cumplan los parámetros elegidos.' }}
                      </p>
                    </div>
                    <div v-if="matchResult.clients_pages > 1" class="camp-pager">
                      <button
                        type="button"
                        class="camp-pager-btn"
                        :disabled="!matchResult.clients_has_previous"
                        @click="goClientPage(-1)"
                      >
                        ANTERIOR
                      </button>
                      <span class="camp-pager-info">
                        PÁGINA {{ matchResult.clients_page }} DE {{ matchResult.clients_pages }}
                      </span>
                      <button
                        type="button"
                        class="camp-pager-btn"
                        :disabled="!matchResult.clients_has_next"
                        @click="goClientPage(1)"
                      >
                        SIGUIENTE
                      </button>
                    </div>

                    <p class="camp-note">
                      SELECCIONE LOS CLIENTES A QUIENES ENVIARÁ LAS OBRAS SELECCIONADAS
                      <span class="camp-note-count">
                        {{ selectedClients.length }} SELECCIONADO{{ selectedClients.length === 1 ? '' : 'S' }}
                      </span>
                    </p>
                    <!-- Marca a TODOS los que cumplen la búsqueda, no sólo a los
                         de esta página: los ids los resuelve el backend. -->
                    <label class="camp-checkbox">
                      <input
                        type="checkbox"
                        :checked="allClientsSelected"
                        :disabled="selectingAll || !matchResult.clients_count"
                        @change="toggleAllClients"
                      />
                      <span>
                        {{ selectingAll ? 'SELECCIONANDO…' : allClientsSelected
                          ? 'QUITAR LA SELECCIÓN'
                          : `SELECCIONAR TODOS (${matchResult.clients_count})` }}
                        <template v-if="clientSearch"> — LOS QUE COINCIDEN CON LA BÚSQUEDA</template>
                      </span>
                    </label>
                  </template>
                </template>

                <!-- Paso 4: previsualización y confirmación -->
                <template v-else>
                  <p class="camp-section-title camp-section-center">
                    PREVISUALIZACIÓN DE LA CAMPAÑA:
                  </p>

                  <div v-if="previewLoading" class="d-flex justify-center py-8">
                    <v-progress-circular indeterminate color="primary" size="36" />
                  </div>
                  <template v-else>
                    <p class="camp-preview-subject">
                      <span>ASUNTO:</span> {{ previewSubject || '—' }}
                    </p>
                    <div class="camp-preview">
                      <iframe
                        class="camp-preview-frame"
                        title="Previsualización del correo"
                        :srcdoc="previewHtml"
                      ></iframe>
                    </div>
                    <p class="camp-preview-note">
                      Es exactamente el correo que recibirán los clientes; en el
                      envío los enlaces suman el seguimiento de aperturas y clics.
                    </p>
                  </template>

                  <!-- Envío de prueba: el mismo correo a una casilla, para
                       revisarlo antes de disparar la campaña de verdad. -->
                  <div class="camp-test">
                    <span class="camp-test-label">ENVIAR UNA PRUEBA A:</span>
                    <input
                      v-model="testEmail"
                      type="email"
                      class="camp-test-input"
                      placeholder="tucorreo@ejemplo.cl"
                      @keyup.enter="sendTest"
                    />
                    <button
                      type="button"
                      class="camp-test-btn"
                      :disabled="testSending"
                      @click="sendTest"
                    >
                      {{ testSending ? 'ENVIANDO…' : 'ENVIAR PRUEBA' }}
                    </button>
                    <span v-if="testResult" class="camp-test-result">{{ testResult }}</span>
                  </div>

                  <dl class="camp-summary">
                    <dt>NOMBRE DE LA CAMPAÑA:</dt>
                    <dd>{{ form.name || '-' }}</dd>
                    <dt>ENVIARÁ INFORMACIÓN DE LA OBRA:</dt>
                    <dd>{{ selectedPlayTitles || '-' }}</dd>
                    <dt>A LOS SIGUIENTES CLIENTES:</dt>
                    <dd>{{ selectedClients.length }} CLIENTES</dd>
                    <dt>CUÁNDO SE ENVÍA:</dt>
                    <dd>EN EL PRÓXIMO ENVÍO DE LAS 09:00</dd>
                  </dl>
                </template>
              </div>

              <div class="camp-actions">
                <button
                  type="button"
                  class="camp-ghost-btn"
                  :disabled="busy"
                  @click="back"
                >
                  {{ step === 1 ? 'CANCELAR Y VOLVER' : 'VOLVER' }}
                </button>

                <template v-if="step < 4">
                  <button type="button" class="camp-primary-btn" @click="next">
                    CONTINUAR
                  </button>
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="camp-dark-btn"
                    :disabled="busy"
                    @click="saveForLater"
                  >
                    {{ saving === 'later' ? 'GUARDANDO…' : 'GUARDAR PARA ENVÍO' }}
                  </button>
                  <button
                    type="button"
                    class="camp-primary-btn"
                    :disabled="busy"
                    @click="sendNow"
                  >
                    {{ saving === 'send' ? 'ENVIANDO…' : sendButtonLabel }}
                  </button>
                </template>
              </div>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  createCampaign,
  fetchCampaign,
  fetchCampaignOptions,
  fetchCampaignRecipients,
  matchCampaign,
  matchCampaignIds,
  previewCampaign,
  saveCampaignForLater,
  sendCampaign,
  sendCampaignTest,
  updateCampaign,
} from "@/services/teatroApi";

const STEP_LABELS = [
  "NOMBRE",
  "PARÁMETROS",
  "SELECCIÓN DE ENVÍO",
  "PREVISUALIZACIÓN Y CONFIRMACIÓN",
];

const MATCH_DESCRIPTIONS = {
  default:
    "ESTA OPCIÓN GENERA MATCH ENTRE TODOS LOS CLIENTES Y OBRAS QUE COMPARTAN " +
    "LOS PARÁMETROS PREVIAMENTE SELECCIONADOS.",
  custom:
    "ESTA OPCIÓN GENERA MATCH ENTRE UNA O MÁS OBRAS SELECCIONADAS Y LOS " +
    "CLIENTES QUE RESULTEN DE LA BÚSQUEDA SEGÚN LOS PARÁMETROS.",
};

export default {
  name: "CampaignForm",
  props: {
    // Al editar una campaña existente.
    campaignId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      step: 1,
      loading: true,
      matching: false,
      previewLoading: false,
      saving: "",
      error: "",
      success: "",
      nameError: "",
      options: {
        genres: [],
        seasons: [],
        stage_surfaces: [],
        age_ranges: [],
        client_types: [],
        match_types: [],
      },
      // Plantilla y oferta no se eligen acá (la campaña usa los valores por
      // defecto del modelo, o los que traiga si se editaron desde el admin),
      // pero se guardan en el formulario y viajan tanto a la previsualización
      // como al guardado: así lo que se ve en el paso 4 es exactamente lo que
      // se envía. La programación sale en la ventana diaria de las 09:00 y el
      // envío forzado se decide después, en el detalle de la campaña.
      form: {
        name: "",
        match_type: "default",
        genre: null,
        season: "all",
        stage_surface: "",
        budget_max: null,
        age_range: "all",
        client_type: "",
        plays_per_email: 1,
        template: "classic",
        offer: "none",
      },
      testEmail: "",
      testSending: false,
      testResult: "",
      matchResult: {
        plays: [], plays_count: 0, plays_page: 1, plays_pages: 1,
        plays_has_next: false, plays_has_previous: false,
        clients: [], clients_count: 0, clients_page: 1, clients_pages: 1,
        clients_has_next: false, clients_has_previous: false,
      },
      // Buscadores y páginas del paso 3. El listado lo pagina el backend: con
      // miles de clientes no se pueden traer todos y filtrar en el navegador.
      playSearch: "",
      clientSearch: "",
      playPage: 1,
      clientPage: 1,
      pageSize: 10,
      searchTimer: null,
      selectingAll: false,
      // Títulos de las obras vistas, para el resumen del paso 4: una obra
      // seleccionada puede haber quedado en otra página.
      playTitles: {},
      selectedPlays: [],
      selectedClients: [],
      previewHtml: "",
      previewSubject: "",
      // Filtros con los que se armó la selección: si cambian, se recalcula; si
      // sólo cambia la página o la búsqueda, la selección se mantiene.
      lastFiltersKey: "",
    };
  },
  computed: {
    isAdmin() {
      return this.$store?.state?.is_admin === true;
    },
    stepLabels() {
      return STEP_LABELS;
    },
    stepTitle() {
      return STEP_LABELS[this.step - 1];
    },
    matchOptions() {
      return (this.options.match_types || []).map((item) => ({
        ...item,
        label: item.label.toUpperCase(),
        description: MATCH_DESCRIPTIONS[item.value] || "",
      }));
    },
    // El primer valor vacío deja el parámetro "sin definir".
    stageSurfaceItems() {
      return [{ value: "", label: "No definido" }, ...(this.options.stage_surfaces || [])];
    },
    clientTypeItems() {
      return [{ value: "", label: "Todos" }, ...(this.options.client_types || [])];
    },
    playsPerEmailItems() {
      return [
        { value: 1, label: "Mailing de 1 obra" },
        { value: 2, label: "Mailing de 2 obras" },
        { value: 3, label: "Mailing de 3 obras" },
      ];
    },
    // Todos los que cumplen la búsqueda, no sólo los de la página visible.
    allClientsSelected() {
      const total = this.matchResult.clients_count || 0;
      return total > 0 && this.selectedClients.length >= total;
    },
    selectedPlayTitles() {
      const byId = new Map(Object.entries(this.playTitles).map(([id, t]) => [Number(id), t]));
      return this.selectedPlays
        .map((id) => byId.get(id))
        .filter(Boolean)
        .join(", ")
        .toUpperCase();
    },
    // `saving` guarda cuál acción está en curso ("send" | "later"), así que NO
    // se puede pasar directo a :disabled: Vue 3 trata la cadena vacía como true
    // en los atributos booleanos (includeBooleanAttr), y los botones nacían
    // deshabilitados.
    busy() {
      return this.saving !== "";
    },
    // Etiqueta del tipo de match fijo, para el aviso de pendiente.
    lockedMatchLabel() {
      const option = this.matchOptions.find((o) => o.value === this.form.match_type);
      return option ? option.label : "PREDETERMINADO";
    },
    sendButtonLabel() {
      // Desde el asistente nunca sale al instante: siempre queda agendada para
      // la ventana de las 09:00. Para adelantarla está el detalle de la campaña.
      return "PROGRAMAR CAMPAÑA";
    },
    // Filtros del paso 2 (sin búsqueda ni página).
    matchFilters() {
      return {
        genre: this.form.genre || null,
        season: this.form.season || "all",
        stage_surface: this.form.stage_surface || "",
        budget_max: this.form.budget_max || null,
        age_range: this.form.age_range || "all",
        client_type: this.form.client_type || "",
      };
    },
    // Lo que define el conjunto de resultados: filtros + buscadores. Es lo que
    // se manda a match/ids para "seleccionar todos".
    matchParams() {
      return {
        ...this.matchFilters,
        play_search: this.playSearch.trim(),
        client_search: this.clientSearch.trim(),
      };
    },
    // Lo anterior más la página que se está mirando.
    matchQuery() {
      return {
        ...this.matchParams,
        play_page: this.playPage,
        client_page: this.clientPage,
        page_size: this.pageSize,
      };
    },
  },
  async created() {
    await this.loadAuth();
    if (!this.isAdmin) {
      this.loading = false;
      return;
    }
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.options = await fetchCampaignOptions();
        if (this.campaignId) {
          await this.loadCampaign();
        }
      } catch (e) {
        this.error = "No se pudo cargar el asistente de campañas.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadCampaign() {
      const campaign = await fetchCampaign(this.campaignId);
      this.form = {
        name: campaign.name,
        match_type: campaign.match_type,
        genre: campaign.genre,
        season: campaign.season || "all",
        stage_surface: campaign.stage_surface || "",
        budget_max: campaign.budget_max,
        age_range: campaign.age_range || "all",
        client_type: campaign.client_type || "",
        plays_per_email: campaign.plays_per_email,
        template: campaign.template || "classic",
        offer: campaign.offer || "none",
      };
      this.selectedPlays = (campaign.plays || []).map((p) => p.id);
      const recipients = await fetchCampaignRecipients(this.campaignId);
      this.selectedClients = recipients.map((r) => r.person).filter(Boolean);
    },
    async runMatch() {
      this.matching = true;
      this.error = "";
      try {
        this.matchResult = await matchCampaign(this.matchQuery);
        // El backend puede devolver la última página si la pedida ya no existe.
        this.playPage = this.matchResult.plays_page || 1;
        this.clientPage = this.matchResult.clients_page || 1;
        this.rememberPlayTitles();

        // La selección sólo se recalcula cuando cambian los filtros del paso 2.
        const filtersKey = JSON.stringify(this.matchFilters);
        if (filtersKey !== this.lastFiltersKey) {
          this.lastFiltersKey = filtersKey;
          await this.applyDefaultSelection();
        }
      } catch (e) {
        this.error = "No se pudo buscar el match de obras y clientes.";
        console.error(e);
      } finally {
        this.matching = false;
      }
    },
    rememberPlayTitles() {
      const titles = { ...this.playTitles };
      for (const play of this.matchResult.plays || []) titles[play.id] = play.title;
      this.playTitles = titles;
    },
    // --- Buscadores -------------------------------------------------------
    onPlaySearch() {
      this.debounceSearch(() => this.searchPlays());
    },
    onClientSearch() {
      this.debounceSearch(() => this.searchClients());
    },
    // Se espera a que deje de escribir para no consultar en cada tecla.
    debounceSearch(run) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(run, 400);
    },
    searchPlays() {
      clearTimeout(this.searchTimer);
      this.playPage = 1;
      this.runMatch();
    },
    searchClients() {
      clearTimeout(this.searchTimer);
      this.clientPage = 1;
      this.runMatch();
    },
    clearPlaySearch() {
      this.playSearch = "";
      this.searchPlays();
    },
    clearClientSearch() {
      this.clientSearch = "";
      this.searchClients();
    },
    // --- Páginas ----------------------------------------------------------
    goPlayPage(delta) {
      this.playPage = Math.max(1, this.playPage + delta);
      this.runMatch();
    },
    goClientPage(delta) {
      this.clientPage = Math.max(1, this.clientPage + delta);
      this.runMatch();
    },
    // El match predeterminado selecciona todo; el personalizado deja elegir.
    // Lo ya seleccionado (campaña que se está editando) se respeta tal cual: con
    // paginación, filtrarlo por lo visible borraría lo elegido en otras páginas.
    async applyDefaultSelection() {
      if (this.selectedPlays.length || this.selectedClients.length) {
        this.selectedPlays = this.selectedPlays.slice(0, this.form.plays_per_email);
        return;
      }
      if (this.form.match_type !== "default") {
        this.selectedPlays = [];
        this.selectedClients = [];
        return;
      }
      // "Predeterminado" es todos los clientes del match, no los de la primera
      // página: los ids completos los da el backend.
      this.selectedPlays = (this.matchResult.plays || [])
        .map((p) => p.id)
        .slice(0, this.form.plays_per_email);
      try {
        const { client_ids: clientIds } = await matchCampaignIds(this.matchParams);
        this.selectedClients = clientIds || [];
      } catch (e) {
        console.error("No se pudieron obtener todos los clientes del match:", e);
        this.selectedClients = (this.matchResult.clients || []).map((c) => c.id);
      }
    },
    togglePlay(id) {
      const index = this.selectedPlays.indexOf(id);
      if (index >= 0) {
        this.selectedPlays.splice(index, 1);
        return;
      }
      if (this.selectedPlays.length >= this.form.plays_per_email) {
        // Al llegar al máximo, la nueva obra reemplaza a la más antigua.
        this.selectedPlays.shift();
      }
      this.selectedPlays.push(id);
    },
    trimSelectedPlays() {
      this.selectedPlays = this.selectedPlays.slice(0, this.form.plays_per_email);
    },
    toggleClient(id) {
      const index = this.selectedClients.indexOf(id);
      if (index >= 0) this.selectedClients.splice(index, 1);
      else this.selectedClients.push(id);
    },
    async toggleAllClients() {
      if (this.allClientsSelected) {
        this.selectedClients = [];
        return;
      }
      // Marca a todos los que cumplen los filtros y la búsqueda actual, aunque
      // estén en otras páginas: el envío va a esa lista completa.
      this.selectingAll = true;
      try {
        const { client_ids: clientIds } = await matchCampaignIds(this.matchParams);
        this.selectedClients = clientIds || [];
      } catch (e) {
        this.error = "No se pudieron seleccionar todos los clientes.";
        console.error(e);
      } finally {
        this.selectingAll = false;
      }
    },
    seasonLabel(play) {
      const format = (value) => {
        if (!value) return "";
        const [year, month, day] = value.split("-");
        return `${day}/${month}/${year.slice(2)}`;
      };
      const start = format(play.start_date);
      const end = format(play.end_date);
      if (start && end) return `${start} - ${end}`;
      return start || end || "SIN TEMPORADA";
    },
    // Lo que define cómo se ve el correo; sirve igual para previsualizar y
    // para el envío de prueba.
    mailPayload() {
      // Todo lo que define el contenido del correo: nombre (va en el asunto),
      // obras, plantilla y oferta especial. Es el mismo set de datos que se
      // guarda en `payload()`, para que la previsualización y el envío de
      // prueba no puedan diferir del correo que reciben los clientes.
      return {
        name: this.form.name,
        play_ids: this.selectedPlays,
        plays_per_email: this.form.plays_per_email,
        template: this.form.template,
        offer: this.form.offer,
      };
    },
    async sendTest() {
      const email = this.testEmail.trim();
      if (!email) {
        this.testResult = "Escribe un correo para enviar la prueba.";
        return;
      }
      this.testSending = true;
      this.testResult = "";
      try {
        await sendCampaignTest({ ...this.mailPayload(), email });
        this.testResult = `Prueba enviada a ${email}.`;
      } catch (e) {
        const data = e.response?.data;
        this.testResult =
          data?.email?.[0] ||
          data?.detail ||
          (Array.isArray(data) ? data[0] : null) ||
          "No se pudo enviar el correo de prueba.";
        console.error(e);
      } finally {
        this.testSending = false;
      }
    },
    async loadPreview() {
      this.previewLoading = true;
      try {
        const { html, subject } = await previewCampaign(this.mailPayload());
        this.previewHtml = html;
        this.previewSubject = subject || "";
      } catch (e) {
        this.error = "No se pudo generar la previsualización.";
        console.error(e);
      } finally {
        this.previewLoading = false;
      }
    },
    validateStep() {
      if (this.step === 1) {
        if (!String(this.form.name || "").trim()) {
          this.nameError = "El nombre de la campaña es obligatorio.";
          return false;
        }
      }
      if (this.step === 3) {
        if (!this.selectedPlays.length) {
          this.error = "Selecciona al menos una obra para el envío.";
          return false;
        }
        if (!this.selectedClients.length) {
          this.error = "Selecciona al menos un cliente para el envío.";
          return false;
        }
      }
      return true;
    },
    async next() {
      this.error = "";
      if (!this.validateStep()) return;
      this.step += 1;
      if (this.step === 3) await this.runMatch();
      if (this.step === 4) await this.loadPreview();
    },
    back() {
      this.error = "";
      if (this.step === 1) {
        window.location.href = "/preventa/";
        return;
      }
      this.step -= 1;
    },
    goToStep(target) {
      // Sólo se permite volver a un paso ya recorrido.
      if (target >= this.step) return;
      this.error = "";
      this.step = target;
    },
    payload() {
      return {
        name: this.form.name,
        match_type: this.form.match_type,
        genre: this.form.genre || null,
        season: this.form.season || "all",
        stage_surface: this.form.stage_surface || "",
        budget_max: this.form.budget_max || null,
        age_range: this.form.age_range || "all",
        client_type: this.form.client_type || "",
        plays_per_email: this.form.plays_per_email,
        play_ids: this.selectedPlays,
        recipient_ids: this.selectedClients,
        template: this.form.template,
        offer: this.form.offer,
      };
    },
    async persist() {
      const body = this.payload();
      if (this.campaignId) {
        return updateCampaign(this.campaignId, body);
      }
      return createCampaign(body);
    },
    apiError(e, fallback) {
      const data = e.response?.data;
      if (typeof data === "string") return fallback;
      if (data?.detail) return data.detail;
      if (Array.isArray(data) && data.length) return data[0];
      const firstKey = data && Object.keys(data)[0];
      const value = firstKey ? data[firstKey] : null;
      if (Array.isArray(value) && value.length) return value[0];
      if (typeof value === "string") return value;
      return fallback;
    },
    async sendNow() {
      if (this.saving) return;
      this.saving = "send";
      this.error = "";
      try {
        const campaign = await this.persist();
        await sendCampaign(campaign.id);
        window.location.href = `/preventa/${campaign.id}/`;
      } catch (e) {
        this.error = this.apiError(e, "No se pudo enviar la campaña.");
        console.error(e);
        this.saving = "";
      }
    },
    async saveForLater() {
      if (this.saving) return;
      this.saving = "later";
      this.error = "";
      try {
        const campaign = await this.persist();
        await saveCampaignForLater(campaign.id);
        window.location.href = "/preventa/";
      } catch (e) {
        this.error = this.apiError(e, "No se pudo guardar la campaña.");
        console.error(e);
        this.saving = "";
      }
    },
  },
};
</script>

<style scoped>
.camp-title {
  max-width: 1080px;
  margin: 0 auto 18px;
}

.camp-title h1 {
  margin: 0;
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1.6px;
  color: var(--color-theater-text);
}

.camp-title h2 {
  margin: 2px 0 0;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1.4px;
  color: rgba(255, 255, 255, 0.6);
}

.camp-steps {
  max-width: 1080px;
  margin: 0 auto 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.camp-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(8, 5, 5, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 7px 16px 7px 8px;
  color: rgba(255, 255, 255, 0.55);
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.9px;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.camp-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  font-size: 10px;
  font-weight: 800;
}

.camp-step-done {
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(200, 16, 46, 0.5);
}

.camp-step-on {
  color: #fff;
  border-color: #c8102e;
}

.camp-step-on .camp-step-num,
.camp-step-done .camp-step-num {
  background: #c8102e;
  color: #fff;
}

.camp-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.camp-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.camp-panel-body {
  padding: 30px 34px 10px;
}

.camp-section-title {
  margin: 0 0 24px;
  font-family: var(--font-family-heading);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1.2px;
  color: #e2334c;
}

.camp-section-center {
  text-align: center;
}

.camp-section-subtitle {
  margin: 0 0 14px;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.1px;
  color: rgba(255, 255, 255, 0.65);
}

.camp-section-spaced {
  margin-top: 34px;
}

/* Etiqueta a la izquierda y control a la derecha, como en los mockups. */
.camp-field {
  display: grid;
  grid-template-columns: 300px minmax(0, 420px);
  gap: 24px;
  align-items: start;
  margin-bottom: 30px;
}

.camp-field-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.camp-field-title {
  font-family: var(--font-family-heading);
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 1px;
  color: var(--color-theater-text);
}

.camp-field-hint {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.6);
}

.camp-note {
  margin: 10px 0 0;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.7px;
  color: rgba(255, 255, 255, 0.6);
}

/* --- Radios del tipo de match ------------------------------------------- */
.camp-radio {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

/* Campo bloqueado a la espera de definición: se ve, no se toca. */
.camp-field-locked .camp-radio {
  cursor: not-allowed;
  opacity: 0.6;
}

.camp-pending {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 0;
  padding: 12px 14px;
  background: rgba(240, 182, 75, 0.12);
  border: 1px solid rgba(240, 182, 75, 0.35);
  border-radius: 10px;
  color: #f0b64b;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.5;
}

.camp-radio:hover {
  background: rgba(255, 255, 255, 0.04);
}

.camp-radio-on {
  border-color: #c8102e;
  background: rgba(200, 16, 46, 0.1);
}

.camp-radio input {
  display: none;
}

.camp-radio-dot {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
}

.camp-radio-on .camp-radio-dot {
  border-color: #e2334c;
  box-shadow: inset 0 0 0 4px #e2334c;
}

.camp-radio-text {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.camp-radio-text strong {
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.9px;
  color: var(--color-theater-text);
}

.camp-radio-text small {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.65);
}

/* --- Tarjetas de obras y clientes ---------------------------------------- */
.camp-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.camp-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 168px;
  max-width: 200px;
  padding: 14px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(200, 16, 46, 0.55);
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.camp-card:hover {
  transform: translateY(-1px);
  border-color: #e2334c;
}

.camp-card-on {
  background: rgba(200, 16, 46, 0.24);
  border-color: #e2334c;
}

.camp-card-title {
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: var(--color-theater-text);
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.camp-card-sub {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.7px;
  color: #ff8497;
  text-transform: uppercase;
}

.camp-card-meta {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
}

.camp-empty-inline {
  margin: 0;
  padding: 12px 0;
  font-family: var(--font-family-heading);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.camp-more-btn {
  margin-top: 14px;
  background: #2e2e2e;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 22px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
}

.camp-more-btn:hover {
  background: #3c3c3c;
}

.camp-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.9px;
  color: var(--color-theater-text);
}


/* --- Buscadores y paginación del paso 3 --------------------------------- */
.camp-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.camp-list-head .camp-section-subtitle {
  margin: 0;
}

.camp-search {
  position: relative;
  display: flex;
  align-items: center;
}

.camp-search-input {
  width: 260px;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  padding: 8px 30px 8px 12px;
  color: #fff;
  font-size: 12.5px;
  outline: none;
}

.camp-search-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.camp-search-input:focus {
  border-color: #c8102e;
}

.camp-search-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}

.camp-search-clear:hover {
  color: #fff;
}

.camp-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 14px 0 0;
}

.camp-pager-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 7px 16px;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  cursor: pointer;
}

.camp-pager-btn:hover:not(:disabled) {
  background: rgba(200, 16, 46, 0.35);
  border-color: #c8102e;
}

.camp-pager-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.camp-pager-info {
  font-family: var(--font-family-heading);
  font-size: 11px;
  letter-spacing: 0.8px;
  color: var(--color-theater-text-muted);
}

/* Cuántos hay elegidos: con listas paginadas la selección no se ve completa. */
.camp-note-count {
  margin-left: 8px;
  color: #e2334c;
  font-weight: 800;
}

/* --- Envío de prueba (paso 5) ------------------------------------------- */
.camp-test {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 680px;
  margin: 24px auto 8px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
}

.camp-test-label {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.9px;
}

.camp-test-input {
  flex: 1 1 200px;
  padding: 9px 16px;
  background: #fff;
  border: none;
  border-radius: 8px;
  outline: none;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 12px;
}

.camp-test-btn {
  background: #2e2e2e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.camp-test-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.camp-test-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.camp-test-result {
  flex: 1 1 100%;
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
}

.camp-checkbox input {
  width: 17px;
  height: 17px;
  accent-color: #c8102e;
  cursor: pointer;
}

/* --- Previsualización ---------------------------------------------------- */
.camp-preview {
  display: flex;
  justify-content: center;
}

.camp-preview-subject {
  max-width: 680px;
  margin: 0 auto 12px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  color: #ffffff;
}

.camp-preview-subject span {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.9px;
  color: rgba(255, 255, 255, 0.6);
  margin-right: 6px;
}

.camp-preview-note {
  max-width: 680px;
  margin: 12px auto 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-theater-text-muted);
}

.camp-preview-frame {
  width: 100%;
  max-width: 680px;
  height: 720px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  background: #ffffff;
}

.camp-summary {
  max-width: 680px;
  margin: 30px auto 0;
}

.camp-summary dt {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.9px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 18px;
}

.camp-summary dd {
  margin: 4px 0 0;
  font-family: var(--font-family-heading);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: var(--color-theater-text);
  text-transform: uppercase;
}

/* --- Acciones ------------------------------------------------------------ */
.camp-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 18px;
  padding: 20px 34px 30px;
}

.camp-primary-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.camp-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.camp-dark-btn {
  background: #2e2e2e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 30px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.camp-dark-btn:hover {
  background: #3c3c3c;
}

.camp-ghost-btn {
  margin-right: auto;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.camp-ghost-btn:hover {
  opacity: 0.72;
}

.camp-primary-btn:disabled,
.camp-dark-btn:disabled,
.camp-ghost-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.camp-denied {
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px;
  text-align: center;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-weight: 600;
  letter-spacing: 0.6px;
}

/* Campos Vuetify blancos, igual que en el resto del panel oscuro. */
.camp-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 999px !important;
}

.camp-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}

.camp-panel :deep(.v-field__input),
.camp-panel :deep(.v-field input) {
  color: #1a1a1a !important;
}

.camp-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}

.camp-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}

.camp-panel :deep(.v-messages),
.camp-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}

@media (max-width: 860px) {
  .camp-field {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .camp-panel-body {
    padding: 24px 18px 8px;
  }

  .camp-actions {
    padding: 16px 18px 26px;
  }
}
</style>
